// src/pages/superadmin/SuperAdminAirports.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2, Plus, Edit2, Trash2, Search, X, CheckCircle, XCircle,
  Key, ChevronLeft, ChevronRight, Cpu, RefreshCw
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const SuperAdminAirports = () => {
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAirport, setEditingAirport] = useState(null);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [airportLicense, setAirportLicense] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updatingAgents, setUpdatingAgents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    timezone: 'America/Chicago',
    max_staff: 50,
    max_owners: 500,
    max_agents: 5,
    expires_at: ''
  });

  useEffect(() => {
    loadAirports();
  }, [page, searchTerm]);

  const loadAirports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/airports/`, {
        params: { page, limit: 20, search: searchTerm }
      });
      setAirports(response.data.airports || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error loading airports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAirportLicense = async (airportId) => {
    try {
      const response = await axios.get(`${API_BASE}/admin/airports/${airportId}/license`);
      setAirportLicense(response.data);
    } catch (error) {
      setAirportLicense(null);
    }
  };

  const handleOpenAgentsModal = async (airport) => {
    setSelectedAirport(airport);
    await loadAirportLicense(airport.id);
    setShowAgentsModal(true);
  };

  const handleUpdateMaxAgents = async () => {
    if (!selectedAirport || !airportLicense) return;
    setUpdatingAgents(true);
    try {
      await axios.put(`/admin/airports/${selectedAirport.id}/license/agents`, {
        max_agents: airportLicense.max_agents
      });
      await loadAirports();
      await loadAirportLicense(selectedAirport.id);
      alert(`✅ Max agents updated to ${airportLicense.max_agents}`);
    } catch (error) {
      alert('❌ Error updating max agents');
    } finally {
      setUpdatingAgents(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.subdomain) {
      alert('Name and subdomain are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingAirport) {
        await axios.put(`/admin/airports/${editingAirport.id}`, formData);
      } else {
        await axios.post('/admin/airports/', formData);
      }
      setShowModal(false);
      resetForm();
      await loadAirports();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving airport');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (airport) => {
    if (window.confirm(`Delete airport "${airport.name}"? This will delete ALL its data!`)) {
      try {
        await axios.delete(`/admin/airports/${airport.id}`);
        await loadAirports();
      } catch (error) {
        alert('Error deleting airport');
      }
    }
  };

  const handleResetPassword = async (airport) => {
    try {
      const response = await axios.post(`/admin/airports/${airport.id}/reset-password`);
      setResetResult({
        airport: airport.name,
        password: response.data.new_password
      });
      setTimeout(() => setResetResult(null), 10000);
    } catch (error) {
      alert('Error resetting password');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subdomain: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      timezone: 'America/Chicago',
      max_staff: 50,
      max_owners: 500,
      max_agents: 5,
      expires_at: ''
    });
    setEditingAirport(null);
  };

  const handleEdit = (airport) => {
    setEditingAirport(airport);
    setFormData({
      name: airport.name || '',
      subdomain: airport.subdomain || '',
      contact_email: airport.contact_email || '',
      contact_phone: airport.contact_phone || '',
      address: airport.address || '',
      timezone: airport.timezone || 'America/Chicago',
      max_staff: airport.max_staff || 50,
      max_owners: airport.max_owners || 500,
      max_agents: airport.max_agents || 5,
      expires_at: airport.license_expires ? airport.license_expires.split('T')[0] : ''
    });
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white">Airports Management</h2>
          <p className="text-gray-400 text-sm">Manage client airports and their agent licenses</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Airport
        </button>
      </div>

      {/* Reset Password Result */}
      {resetResult && (
        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
          <p className="text-green-400 font-medium">Password reset for {resetResult.airport}</p>
          <p className="text-white text-lg font-mono mt-1">{resetResult.password}</p>
          <p className="text-gray-400 text-xs mt-1">Give this password to the airport administrator.</p>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Search airports by name, subdomain, or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white"
        />
      </div>

      {/* Airports Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr className="text-gray-400 text-sm">
                <th className="p-4 text-left">Airport</th>
                <th className="p-4 text-left">Subdomain</th>
                <th className="p-4 text-left">Contact</th>
                <th className="p-4 text-left">Agents</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {airports.map((airport) => (
                <tr key={airport.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  <td className="p-4">
                    <div>
                      <p className="text-white font-medium">{airport.name}</p>
                      <p className="text-gray-500 text-xs">ID: {airport.id.substring(0, 8)}...</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <code className="text-blue-400 text-sm">{airport.subdomain}.aerotrick.com</code>
                  </td>
                  <td className="p-4">
                    <p className="text-gray-300 text-sm">{airport.contact_email || '-'}</p>
                    <p className="text-gray-500 text-xs">{airport.contact_phone || '-'}</p>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                      {airport.used_agents || 0}/{airport.max_agents || 5}
                    </span>
                  </td>
                  <td className="p-4">
                    {airport.is_active ? (
                      <span className="flex items-center gap-1 text-green-400 text-xs">
                        <CheckCircle className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-400 text-xs">
                        <XCircle className="w-3 h-3" /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(airport)} className="p-1.5 hover:bg-gray-700 rounded-lg text-blue-400" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleOpenAgentsModal(airport)} className="p-1.5 hover:bg-gray-700 rounded-lg text-purple-400" title="Manage Agents">
                        <Cpu className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleResetPassword(airport)} className="p-1.5 hover:bg-gray-700 rounded-lg text-yellow-400" title="Reset Password">
                        <Key className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(airport)} className="p-1.5 hover:bg-gray-700 rounded-lg text-red-400" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-gray-700">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 disabled:opacity-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-gray-400 text-sm py-2">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 disabled:opacity-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Modal Agents */}
      {showAgentsModal && selectedAirport && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">Agents for {selectedAirport.name}</h3>
              <button onClick={() => setShowAgentsModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">License Key:</span>
                  <code className="text-blue-400 text-sm">{airportLicense?.license_key}</code>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Agents Used:</span>
                  <span className="text-white font-medium">{airportLicense?.used_agents || 0} / {airportLicense?.max_agents || 5}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Max Agents:</span>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" max="100" value={airportLicense?.max_agents || 5}
                      onChange={(e) => setAirportLicense(prev => ({ ...prev, max_agents: parseInt(e.target.value) }))}
                      className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm" />
                    <button onClick={handleUpdateMaxAgents} disabled={updatingAgents}
                      className="px-3 py-1 bg-purple-500 hover:bg-purple-600 rounded text-white text-sm">
                      {updatingAgents ? 'Updating...' : 'Update'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-400 text-sm font-medium mb-2">📋 Installation Instructions</p>
                <code className="block bg-gray-900 p-2 rounded mt-2 text-blue-400 text-sm break-all">
                  {airportLicense?.license_key}
                </code>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button onClick={() => setShowAgentsModal(false)} className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">{editingAirport ? 'Edit Airport' : 'New Airport'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Airport Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Subdomain *</label>
                  <input type="text" value={formData.subdomain} onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                  <p className="text-xs text-gray-500 mt-1">.aerotrick.com</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contact Email</label>
                  <input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contact Phone</label>
                  <input type="tel" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Address</label>
                <textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows="2" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Staff</label>
                  <input type="number" value={formData.max_staff} onChange={(e) => setFormData({ ...formData, max_staff: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Owners</label>
                  <input type="number" value={formData.max_owners} onChange={(e) => setFormData({ ...formData, max_owners: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Agents</label>
                  <input type="number" value={formData.max_agents} onChange={(e) => setFormData({ ...formData, max_agents: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button onClick={handleSubmit} disabled={submitting}
                className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium disabled:opacity-50">
                {submitting ? 'Saving...' : (editingAirport ? 'Update' : 'Create')}
              </button>
              <button onClick={() => setShowModal(false)} className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminAirports;